import {
  ABILITY_DEFS, skillParams, type AbilityInstance, type SkillParams,
} from './AbilityDefs';
import type { World } from '../World';
import { dist2, TAU } from '../../core/math';
import type { Enemy } from '../entities/Enemy';
import { skillOutgoingDamageMult } from './skillHooks';
import { SHOT_KINDS, spawnPlayerShot } from './shots';
import { LEECH_DRINK, LEECH_DRINK_MAX_HITS, LEECH_DRINK_UPGRADED, SUNDER_HASTE } from '../characters/aspects';

/** Censer flame contact body (tuning): the orbiting flame burns what it
 *  sweeps through. Radius scales ×areaMult; the shared per-instance gate is
 *  the single-target melt guard (Orbit pattern). */
/** Trail of Embers (censer upgrade): ground dps as a fraction of aura dps. */
export const TRAIL_DPS_FACTOR = 0.6;

/** Chill: slow factor while chilled and bonus-damage multiplier. */
export const RIME_SLOW = 0.6;
export const RIME_AMP = 1.25;
/** Deep Freeze (Rime upgrade): chilled regulars below this HP fraction shatter. */
export const RIME_SHATTER_FRAC = 0.2;
/** Twin Star's second volley damage share (trim 2026-07-28). */
export const TWIN_STAR_SECOND_FACTOR = 0.7;

/** A missing archetype case must be a compile error, never a silent no-op. */
function assertNever(x: never): never {
  throw new Error(`unhandled archetype: ${String(x)}`);
}

/**
 * Ticks owned skills. All aiming reads live positions (reactive, no rng) —
 * skills are intentionally outside the seeded-determinism boundary.
 */
export class AbilitySystem {
  update(world: World, dt: number): void {
    const abilities = world.runState.abilities;
    const stats = world.runState.stats;
    // Sunder (Trine): the split form fires hastened — the burst window.
    const sunderHaste = world.sunderLeft > 0 ? 1 / SUNDER_HASTE : 1;
    const cdMult = stats.resolve('cooldownMult') * sunderHaste;
    // Live skill-hook multiplier (Tithe, aspects) folds in here — one site.
    const dmgMult = stats.resolve('damageMult') * skillOutgoingDamageMult(world.runState);
    const areaMult = stats.resolve('areaMult');

    this.steerSwarmMotes(world, dt);

    for (const inst of abilities) {
      const def = ABILITY_DEFS[inst.defId];
      const p = skillParams(inst);

      // Continuous archetypes run every tick, outside the cooldown gate.
      if (def.archetype === 'orbit') {
        this.updateOrbit(world, inst, p, dt, dmgMult, areaMult);
        continue;
      }
      if (def.archetype === 'censer') {
        this.updateCenser(world, inst, p, dt, dmgMult, areaMult);
        continue;
      }

      inst.cooldown -= dt;
      if (inst.cooldown > 0) continue;
      inst.cooldown = p.cooldown * cdMult;

      switch (def.archetype) {
        case 'projectile':
          this.fireBolts(world, inst, p, dmgMult, stats.resolve('projSpeedMult'));
          break;
        case 'undertow':
          this.fireUndertow(world, inst, p, dmgMult, areaMult);
          break;
        case 'rime':
          this.fireRime(world, inst, p, areaMult);
          break;
        case 'ricochet':
          this.fireRicochet(world, p, dmgMult, stats.resolve('projSpeedMult'));
          break;
        case 'swarm':
          this.fireSwarm(world, inst, p, cdMult, dmgMult, stats.resolve('projSpeedMult'));
          break;
        case 'hooked':
          break; // no tick — lives at World funnels via skillHooks/aspects
        case 'fanKnives':
          this.fireFanKnives(world, inst, p, dmgMult, stats.resolve('projSpeedMult'),
            stats.resolve('moveSpeed'));
          break;
        case 'cleave':
          this.fireCleave(world, inst, p, dmgMult, areaMult);
          break;
        case 'prismLance':
          this.firePrismLance(world, p, dmgMult, stats.resolve('projSpeedMult'));
          break;
        case 'boreCharge':
          this.fireBoreCharge(world, p, dmgMult, areaMult, stats.resolve('projSpeedMult'));
          break;
        case 'hollowShot':
          this.fireHollowShot(world, p, dmgMult, stats.resolve('projSpeedMult'));
          break;
        case 'leechChain':
          this.fireLeechChain(world, inst, p, dmgMult);
          break;
        case 'witchlight':
          this.fireWitchlight(world, inst, p, dmgMult);
          break;
        default:
          assertNever(def.archetype);
      }
    }
  }

  private nearestEnemy(world: World, range: number, exclude: Enemy | null = null): Enemy | null {
    const p = world.player;
    let best: Enemy | null = null;
    let bestD = range * range;
    for (const e of world.enemies) {
      if (e === exclude) continue;
      const d = dist2(p.x, p.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        best = e;
      }
    }
    return best;
  }

  /**
   * Lodestar (Hale's signature): aimed volley at the nearest enemy. Twin Star
   * upgrade fires a second aimed volley at the second-nearest foe (falling
   * back on the first) — unconditional value, never positional. The second
   * volley fires at TWIN_STAR_SECOND_FACTOR (trim 2026-07-28: the card was
   * doubling him to the top of the probe table; ~136 k/min keeps it
   * transformative without the crown).
   */
  private fireBolts(
    world: World, inst: AbilityInstance, p: SkillParams, dmgMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 620);
    if (!target) return;
    const pl = world.player;
    const targets = [target];
    if (inst.upgraded) targets.push(this.nearestEnemy(world, 620, target) ?? target);
    targets.forEach((t, ti) => {
      const dir = Math.atan2(t.y - pl.y, t.x - pl.x);
      const dmg = p.damage * dmgMult * (ti === 1 ? TWIN_STAR_SECOND_FACTOR : 1);
      for (let i = 0; i < p.count; i++) {
        const off = p.count > 1 ? -0.09 * (p.count - 1) / 2 + 0.09 * i : 0;
        spawnPlayerShot(world, pl.x, pl.y, dir + off, 560 * speedMult, SHOT_KINDS.bolt, dmg);
      }
    });
  }

  private nearbyCount(world: World, radius: number): number {
    const p = world.player;
    let n = 0;
    world.hash.query(p.x, p.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (e && e.alive && dist2(p.x, p.y, e.x, e.y) <= radius * radius) n++;
    });
    return n;
  }

  /**
   * Ricochet: aimed shot whose charge count (p1) grows with the local crowd —
   * bounce resolution lives in World.collideShotsVsEnemies.
   */
  private fireRicochet(
    world: World, p: SkillParams, dmgMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 620);
    if (!target) return;
    const pl = world.player;
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    const bounces = p.count + Math.min(4, Math.floor(this.nearbyCount(world, p.radius) / 5));
    spawnPlayerShot(world, pl.x, pl.y, aim, 430 * speedMult, SHOT_KINDS.ricochet,
      p.damage * dmgMult, { radius: 5, lifeSteps: 150, scale: 0.9, charges: bounces });
  }

  /**
   * Swarm: homing motes whose cadence scales with the local crowd (the
   * per-tick steering pass below owns the homing). Carrion Motes (upgrade)
   * carry one re-seek charge, spent only on a killing hit.
   */
  private fireSwarm(
    world: World, inst: AbilityInstance, p: SkillParams,
    cdMult: number, dmgMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 620);
    if (!target) return;
    const pl = world.player;
    const nearby = this.nearbyCount(world, p.radius);
    // Density IS the fire rate: refresh the cooldown against the live crowd.
    inst.cooldown = (p.cooldown * cdMult) / (1 + nearby / 4);
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    for (let i = 0; i < p.count; i++) {
      spawnPlayerShot(world, pl.x, pl.y, aim + (i - (p.count - 1) / 2) * 0.3,
        260 * speedMult, SHOT_KINDS.swarm, p.damage * dmgMult,
        { radius: 4, lifeSteps: 160, scale: 0.8, charges: inst.upgraded ? 1 : 0 });
    }
  }

  /** Per-tick homing for live swarm motes: curve toward the nearest enemy. */
  private steerSwarmMotes(world: World, dt: number): void {
    const pool = world.playerShots;
    const TURN = 7; // rad/s
    for (let i = 0; i < pool.count; i++) {
      if (pool.kind[i] !== SHOT_KINDS.swarm) continue;
      const sx = pool.x[i]!;
      const sy = pool.y[i]!;
      let tx = 0;
      let ty = 0;
      let bestD = 320 * 320;
      let found = false;
      world.hash.query(sx, sy, 320, (idx) => {
        const e = world.enemies[idx];
        if (!e || !e.alive) return;
        const d = dist2(sx, sy, e.x, e.y);
        if (d < bestD) {
          bestD = d;
          tx = e.x;
          ty = e.y;
          found = true;
        }
      });
      if (!found) continue;
      const vx = pool.vx[i]!;
      const vy = pool.vy[i]!;
      const speed = Math.hypot(vx, vy) || 1;
      const want = Math.atan2(ty - sy, tx - sx);
      const have = Math.atan2(vy, vx);
      let delta = want - have;
      while (delta > Math.PI) delta -= TAU;
      while (delta < -Math.PI) delta += TAU;
      const turn = Math.max(-TURN * dt, Math.min(TURN * dt, delta));
      pool.vx[i] = Math.cos(have + turn) * speed;
      pool.vy[i] = Math.sin(have + turn) * speed;
    }
  }

  /**
   * Undertow: pulse that drags enemies INWARD (wall-guarded, never the boss)
   * with token damage. Spring Tide upgrade drags loose gems in too.
   */
  private fireUndertow(
    world: World, inst: AbilityInstance, p: SkillParams, dmgMult: number, areaMult: number,
  ): void {
    const pl = world.player;
    const radius = p.radius * areaMult;
    let any = false;
    world.hash.query(pl.x, pl.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(pl.x, pl.y, e.x, e.y) > (radius + e.radius) ** 2) return;
      world.damageEnemy(idx, p.damage * dmgMult);
      any = true;
      if (e.type === 'boss' || !e.alive) return;
      const d = Math.sqrt(dist2(pl.x, pl.y, e.x, e.y)) || 1;
      const pull = Math.min(p.count, Math.max(0, d - pl.radius - e.radius));
      const nx = e.x - ((e.x - pl.x) / d) * pull;
      const ny = e.y - ((e.y - pl.y) / d) * pull;
      if (!world.tileMap.circleCollides(nx, ny, e.radius)) {
        e.x = nx;
        e.y = ny;
      }
    });
    if (inst.upgraded) {
      const gems = world.gems;
      for (let i = 0; i < gems.count; i++) {
        const gx = gems.x[i]!;
        const gy = gems.y[i]!;
        const d2 = dist2(pl.x, pl.y, gx, gy);
        if (d2 > radius * radius) continue;
        const d = Math.sqrt(d2) || 1;
        const pull = Math.min(p.count * 2, d);
        gems.x[i] = gx - ((gx - pl.x) / d) * pull;
        gems.y[i] = gy - ((gy - pl.y) / d) * pull;
      }
    }
    world.events.emit('undertowPulse', { x: pl.x, y: pl.y, radius, hit: any });
  }

  /**
   * Rime: cadenced chill — slowed (RIME_SLOW) + bonus damage (RIME_AMP at the
   * damageEnemy funnel) for p.duration seconds. Boss/Keeper are chill-immune
   * (set-piece pacing). Deep Freeze marks the chill so low regulars shatter.
   */
  private fireRime(world: World, inst: AbilityInstance, p: SkillParams, areaMult: number): void {
    const pl = world.player;
    const radius = p.radius * areaMult;
    let any = false;
    world.hash.query(pl.x, pl.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (e.type === 'boss' || e.type === 'keeper') return;
      if (dist2(pl.x, pl.y, e.x, e.y) <= (radius + e.radius) ** 2) {
        e.chillLeft = Math.max(e.chillLeft, p.duration);
        e.chillShatter = inst.upgraded;
        any = true;
      }
    });
    world.events.emit('rimePulse', { x: pl.x, y: pl.y, radius, hit: any });
  }

  /**
   * Orbit: circling bodies with per-body contact damage on a shared tick —
   * hits more often the denser the crowd. Bulwark Ring upgrade shoves what
   * it touches AND eats enemy bullets (the shove is the melee-first half —
   * post-roster-rework, bullets alone were a near-dead upgrade).
   */
  private updateOrbit(
    world: World, inst: AbilityInstance, p: SkillParams,
    dt: number, dmgMult: number, areaMult: number,
  ): void {
    const pl = world.player;
    inst.angle = (inst.angle + 2.4 * dt) % TAU;
    if (inst.contactTick > 0) inst.contactTick -= dt;
    const orbitR = p.radius * areaMult;
    const bodyR = 15;

    for (let w = 0; w < p.count; w++) {
      const a = inst.angle + (w * TAU) / p.count;
      const wx = pl.x + Math.cos(a) * orbitR;
      const wy = pl.y + Math.sin(a) * orbitR;

      if (inst.upgraded) {
        const pool = world.enemyBullets;
        for (let i = 0; i < pool.count; i++) {
          const rr = bodyR + pool.radius[i]!;
          if (dist2(pool.x[i]!, pool.y[i]!, wx, wy) <= rr * rr) {
            world.spawnSparks(pool.x[i]!, pool.y[i]!, 2, 0.6);
            pool.despawn(i);
            i--;
          }
        }
      }

      if (inst.contactTick <= 0) {
        world.hash.query(wx, wy, bodyR + 30, (idx) => {
          const e = world.enemies[idx];
          if (!e || !e.alive) return;
          if (dist2(wx, wy, e.x, e.y) <= (bodyR + e.radius) ** 2) {
            world.damageEnemy(idx, p.damage * dmgMult);
            inst.contactTick = p.cooldown;
            // Bulwark Ring: shove the contacted body away (wall-guarded,
            // never boss-class) — the Cleave knockback pattern.
            if (inst.upgraded && e.alive && e.type !== 'boss' && e.type !== 'keeper') {
              const d = Math.sqrt(dist2(pl.x, pl.y, e.x, e.y)) || 1;
              const nx = e.x + ((e.x - pl.x) / d) * 40;
              const ny = e.y + ((e.y - pl.y) / d) * 40;
              if (!world.tileMap.circleCollides(nx, ny, e.radius)) {
                e.x = nx;
                e.y = ny;
              }
            }
          }
        });
      }
    }
  }

  /**
   * Censer (Wick) — a smoldering AURA around him (rework 2026-07-27:
   * Sarah's call — the orbiting flame's placement was too awkward).
   * Everything inside the radius burns at p.damage dps, kind 'dot' (aura
   * damage never stacks Fracture shred — the Caustic rule). No wall check:
   * it is smoke, it wraps corners. Upgrade — Trail of Embers: walking
   * paints burning ground at HIS OWN position (placement by walking; burn
   * zones are max-dps, so overlapping drops never stack).
   */
  private updateCenser(
    world: World, inst: AbilityInstance, p: SkillParams,
    dt: number, dmgMult: number, areaMult: number,
  ): void {
    const pl = world.player;
    inst.angle = (inst.angle + 3.0 * dt) % TAU; // render only: ember drift phase
    const auraR = p.radius * areaMult;
    world.hash.query(pl.x, pl.y, auraR + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(pl.x, pl.y, e.x, e.y) <= (auraR + e.radius) ** 2) {
        world.damageEnemy(idx, p.damage * dmgMult * dt, 'dot');
      }
    });

    if (!inst.upgraded) return;
    inst.contactTick -= dt;
    if (inst.contactTick > 0) return;
    inst.contactTick = p.cooldown;
    world.zones.push({
      x: pl.x,
      y: pl.y,
      radius: 40 * areaMult,
      duration: p.duration,
      dps: p.damage * TRAIL_DPS_FACTOR * dmgMult,
      slowPct: 0,
      kind: 'burn',
    });
    // Cap the trail so a stationary Wick doesn't flood the zone list.
    let burns = 0;
    for (let z = world.zones.length - 1; z >= 0; z--) {
      if (world.zones[z]!.kind !== 'burn') continue;
      burns++;
      if (burns > 10) world.zones.splice(z, 1);
    }
  }

  /**
   * Fan of Knives (Vane): cone burst whose knife count scales with LIVE move
   * speed — the aspect axis made into a weapon. Ninefold Fan mirrors it.
   */
  private fireFanKnives(
    world: World, inst: AbilityInstance, p: SkillParams,
    dmgMult: number, speedMult: number, moveSpeed: number,
  ): void {
    const target = this.nearestEnemy(world, 480);
    if (!target) return;
    const pl = world.player;
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    const knives = p.count + Math.max(0, Math.round((moveSpeed / 260 - 1) * 20));
    const halfCone = p.radius / 100;
    const dirs = inst.upgraded ? [aim, aim + Math.PI] : [aim];
    for (const dir of dirs) {
      for (let k = 0; k < knives; k++) {
        const off = knives > 1 ? -halfCone + (2 * halfCone * k) / (knives - 1) : 0;
        spawnPlayerShot(world, pl.x, pl.y, dir + off, 520 * speedMult, SHOT_KINDS.bolt,
          p.damage * dmgMult, { lifeSteps: 42 });
      }
    }
  }

  /**
   * Cleave (Barrow): spinning strike that loves a crowd — damage grows
   * +15% per extra body in reach. Whirlbreaker shoves them back.
   */
  private fireCleave(
    world: World, inst: AbilityInstance, p: SkillParams, dmgMult: number, areaMult: number,
  ): void {
    const pl = world.player;
    const radius = p.radius * areaMult;
    let bodies = 0;
    world.hash.query(pl.x, pl.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (e && e.alive && dist2(pl.x, pl.y, e.x, e.y) <= (radius + e.radius) ** 2) bodies++;
    });
    if (bodies === 0) return;
    const dmg = p.damage * (1 + 0.15 * (bodies - 1)) * dmgMult;
    world.hash.query(pl.x, pl.y, radius + 30, (idx) => {
      const e = world.enemies[idx];
      if (!e || !e.alive) return;
      if (dist2(pl.x, pl.y, e.x, e.y) > (radius + e.radius) ** 2) return;
      world.damageEnemy(idx, dmg);
      if (inst.upgraded && e.alive && e.type !== 'boss') {
        const d = Math.sqrt(dist2(pl.x, pl.y, e.x, e.y)) || 1;
        const nx = e.x + ((e.x - pl.x) / d) * 40;
        const ny = e.y + ((e.y - pl.y) / d) * 40;
        if (!world.tileMap.circleCollides(nx, ny, e.radius)) {
          e.x = nx;
          e.y = ny;
        }
      }
    });
    world.events.emit('burstFired', { x: pl.x, y: pl.y, radius });
  }

  private firePrismLance(
    world: World, p: SkillParams, dmgMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 620);
    if (!target) return;
    const pl = world.player;
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    spawnPlayerShot(world, pl.x, pl.y, aim, 340 * speedMult, SHOT_KINDS.prism,
      p.damage * dmgMult, { radius: 5, lifeSteps: 120, scale: 1.0, charges: p.count });
  }

  private fireBoreCharge(
    world: World, p: SkillParams, dmgMult: number, areaMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 560);
    if (!target) return;
    const pl = world.player;
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    for (let i = 0; i < p.count; i++) {
      const off = p.count > 1 ? -0.2 + 0.4 * i : 0;
      spawnPlayerShot(world, pl.x, pl.y, aim + off, 260 * speedMult, SHOT_KINDS.bore,
        p.damage * dmgMult, {
          radius: 7, lifeSteps: 150, scale: 1.3, splash: p.radius * areaMult,
        });
    }
  }

  private fireHollowShot(
    world: World, p: SkillParams, dmgMult: number, speedMult: number,
  ): void {
    const target = this.nearestEnemy(world, 720);
    if (!target) return;
    const pl = world.player;
    const aim = Math.atan2(target.y - pl.y, target.x - pl.x);
    spawnPlayerShot(world, pl.x, pl.y, aim, 300 * speedMult, SHOT_KINDS.hollow,
      p.damage * dmgMult, { radius: 6, lifeSteps: 170, scale: 1.2, charges: p.count });
  }

  /**
   * Leech Chain (Thirl): hitscan whip — yank each link toward him and drink.
   * The drink routes through Player.heal('leech'): under Ebb it is his ONLY
   * recovery. Enemy indices are stable (compaction is end-of-update).
   */
  private fireLeechChain(
    world: World, inst: AbilityInstance, p: SkillParams, dmgMult: number,
  ): void {
    const pl = world.player;
    const enemies = world.enemies;
    let first = -1;
    let bestD = 320 * 320;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i]!;
      if (!e.alive) continue;
      const d = dist2(pl.x, pl.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        first = i;
      }
    }
    if (first < 0) return;
    world.leechQuiet = 0; // a connection — Ebb's quiet-drain clock resets
    // Bloodprice (actives.ts): the paid-for chain drinks deep — consumed
    // only when a chain actually connects.
    const surge = world.bloodprice;
    if (surge) world.bloodprice = false;
    const jumps = p.count + (surge ? 2 : 0);
    const seek = p.radius + (surge ? 40 : 0);
    const drink = (inst.upgraded ? LEECH_DRINK_UPGRADED : LEECH_DRINK) * (surge ? 2 : 1);
    const dmgScale = surge ? 2.5 : 1;
    const visited = new Set<number>();
    const points: { x: number; y: number }[] = [{ x: pl.x, y: pl.y }];
    let current = first;
    let totalDrink = 0;
    for (let k = 0; k <= jumps && current >= 0; k++) {
      const target = enemies[current]!;
      visited.add(current);
      points.push({ x: target.x, y: target.y });
      world.damageEnemy(current, p.damage * dmgScale * dmgMult * Math.pow(0.85, k));
      // The drink caps at the first LEECH_DRINK_MAX_HITS links (damage does
      // NOT) — dense floors stop scaling his sustain, so depth threatens
      // Thirl like everyone else (probe 2026-07-27: 0 deaths in 15 runs).
      const drinks = k < LEECH_DRINK_MAX_HITS;
      if (target.alive && target.type !== 'boss' && target.type !== 'keeper') {
        // The yank: drag the link toward Thirl (wall-guarded).
        const d = Math.sqrt(dist2(pl.x, pl.y, target.x, target.y)) || 1;
        const pull = Math.min(36, Math.max(0, d - pl.radius - target.radius));
        const nx = target.x - ((target.x - pl.x) / d) * pull;
        const ny = target.y - ((target.y - pl.y) / d) * pull;
        if (!world.tileMap.circleCollides(nx, ny, target.radius)) {
          target.x = nx;
          target.y = ny;
        }
      }
      if (drinks) totalDrink += drink;
      let next = -1;
      let nextD = seek * seek;
      for (let i = 0; i < enemies.length; i++) {
        if (visited.has(i)) continue;
        const e = enemies[i]!;
        if (!e.alive) continue;
        const d = dist2(target.x, target.y, e.x, e.y);
        if (d < nextD) {
          nextD = d;
          next = i;
        }
      }
      current = next;
    }
    if (totalDrink > 0) {
      const healed = pl.heal(totalDrink, 'leech');
      if (healed > 0) world.events.emit('playerHealed', { amount: healed });
    }
    world.events.emit('chainLash', { points, kind: 'leech' });
  }

  /**
   * Witchlight (pool, 2026-07-28): a stray grave-light leaping foe to foe —
   * leechChain's hitscan shape with none of Thirl's plumbing (no drink, no
   * yank, no quiet-clock). Fades ×0.85 per jump; Undimmed doesn't.
   */
  private fireWitchlight(
    world: World, inst: AbilityInstance, p: SkillParams, dmgMult: number,
  ): void {
    const pl = world.player;
    const enemies = world.enemies;
    let first = -1;
    let bestD = 320 * 320;
    for (let i = 0; i < enemies.length; i++) {
      const e = enemies[i]!;
      if (!e.alive) continue;
      const d = dist2(pl.x, pl.y, e.x, e.y);
      if (d < bestD) {
        bestD = d;
        first = i;
      }
    }
    if (first < 0) return;
    const visited = new Set<number>();
    const points: { x: number; y: number }[] = [{ x: pl.x, y: pl.y }];
    let current = first;
    for (let k = 0; k <= p.count && current >= 0; k++) {
      const target = enemies[current]!;
      visited.add(current);
      points.push({ x: target.x, y: target.y });
      world.damageEnemy(current, p.damage * dmgMult * (inst.upgraded ? 1 : 0.85 ** k));
      let next = -1;
      let nextD = p.radius * p.radius;
      for (let i = 0; i < enemies.length; i++) {
        if (visited.has(i)) continue;
        const e = enemies[i]!;
        if (!e.alive) continue;
        const d = dist2(target.x, target.y, e.x, e.y);
        if (d < nextD) {
          nextD = d;
          next = i;
        }
      }
      current = next;
    }
    world.events.emit('chainLash', { points, kind: 'arc' });
  }

  /** Orbit body world positions for rendering. */
  static orbitPositions(
    inst: AbilityInstance, px: number, py: number, areaMult: number,
  ): [number, number][] {
    const def = ABILITY_DEFS[inst.defId];
    if (def.archetype !== 'orbit') return [];
    const p = skillParams(inst);
    const out: [number, number][] = [];
    const orbitR = p.radius * areaMult;
    for (let w = 0; w < p.count; w++) {
      const a = inst.angle + (w * TAU) / p.count;
      out.push([px + Math.cos(a) * orbitR, py + Math.sin(a) * orbitR]);
    }
    return out;
  }
}
